import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MapPin, Calendar, AlertTriangle } from "lucide-react";
import { ClaimDetailed } from "@/api/generated/client/models";
import { EditableField } from "@/components/ui/editable-field";
import { useSidebarValidation } from "@/hooks/use-sidebar-validation";
import { format } from "date-fns";
import { dateToLocalDateString, parseDateString } from "@/lib/utils";

interface SidebarPropertySectionProps {
    claim: ClaimDetailed;
    onUpdate: (field: string, value: any) => Promise<void>;
    canWrite: boolean;
}

export function SidebarPropertySection({ claim, onUpdate, canWrite }: SidebarPropertySectionProps) {
    const { validateAndSave } = useSidebarValidation();
    return (
        <Card className="shadow-xs bg-card">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold">Loss Information</h2>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Property Address</p>
                        <EditableField
                            value={claim.propertyAddress}
                            onSave={(val) => validateAndSave('propertyAddress', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Property Address"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Date of Loss</p>
                        <EditableField
                            value={dateToLocalDateString(claim.dateOfLoss)}
                            onSave={(val) => validateAndSave('dateOfLoss', val || null, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium"
                            placeholder="Select Date"
                            type="date"
                            formatValue={(val) => {
                                if (!val) return 'Select Date';
                                const localDate = parseDateString(val);
                                return format(localDate, "MMM d, yyyy");
                            }}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Cause of Loss</p>
                        <EditableField
                            value={claim.lossType || ''}
                            onSave={(newValue) => validateAndSave('lossType', newValue, onUpdate)}
                            disabled={!canWrite}
                            placeholder="Enter cause of loss"
                            className="text-sm font-medium"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
